/**
 * escalationDiscernmentEngine.js
 *
 * Determines PASTORAL STRATEGY escalation — when and how the response
 * strategy should change in scope or involve others.
 *
 * DISTINCT FROM ethics layer escalation:
 *   Ethics layer → danger signals (harm, abuse, crisis) — immediate safety
 *   THIS LAYER  → pastoral strategy (involve leadership, recommend rest, refer,
 *                  increase follow-up cadence, de-escalate) — relational wisdom
 *
 * Types:
 *   maintain            — no change needed
 *   increase_followup   — situation warrants more frequent check-ins
 *   involve_leadership  — pastoral situation needs broader team awareness
 *   recommend_rest      — shepherd/leader needs to step back temporarily
 *   refer_professional  — pastoral limits reached; counseling/specialist needed
 *   increase_prudence   — proceed carefully; complexity demands more caution
 *   de_escalate         — situation is improving; reduce intensity
 */

/**
 * @param {{
 *   riskLevel:          string,
 *   conflictType:       string,
 *   congregationSignal: string,
 *   loadState:          string,
 *   regressionState:    string,
 *   processState:       string,
 *   complexity:         string,
 *   conversationLength: number,
 * }} params
 * @returns {{
 *   escalation:  string,
 *   instruction: string|null,
 * }}
 */
export function resolveEscalation({
  riskLevel          = "none",
  conflictType       = "ninguno",
  congregationSignal = "ninguno",
  loadState          = "low",
  regressionState    = "stable",
  processState       = "apertura",
  complexity         = "simple",
  conversationLength = 0,
}) {
  // Ethics layer already handles critical risk — don't double-fire here
  // This layer handles the "adjacent to crisis" pastoral territory

  // Professional referral when complexity is deeply_complex + risk elevated
  if (
    complexity === "deeply_complex" &&
    (riskLevel === "high" || riskLevel === "elevated")
  ) {
    return {
      escalation: "refer_professional",
      instruction: "La situación supera el alcance pastoral solo. Facilita conexión con consejería o apoyo especializado.",
    };
  }

  // Leadership involvement for active congregational conflict
  if (
    conflictType === "liderazgo" ||
    congregationSignal === "tension_liderazgo" ||
    congregationSignal === "fragmentacion_relacional"
  ) {
    return {
      escalation: "involve_leadership",
      instruction: "La situación toca dinámicas de liderazgo o congregación. Puede requerir discernimiento colectivo.",
    };
  }

  // Rest for pastoral overload
  if (loadState === "high" || processState === "agotamiento_espiritual") {
    return {
      escalation: "recommend_rest",
      instruction: "El agotamiento es una señal seria. La invitación al descanso es pastoral, no debilidad.",
    };
  }

  // Sustained regression warrants increased follow-up
  if (regressionState === "sustained_regression" || regressionState === "regression") {
    return {
      escalation: "increase_followup",
      instruction: "El retroceso continuado requiere mayor acompañamiento. Aumenta la frecuencia de contacto pastoral.",
    };
  }

  // Prudence when complexity or doctrinal conflict elevated
  if (
    complexity === "delicate" ||
    conflictType === "doctrinal" ||
    congregationSignal === "agotamiento_colectivo"
  ) {
    return {
      escalation: "increase_prudence",
      instruction: null,
    };
  }

  // De-escalate when restoration is sustained
  if (regressionState === "sustained_restoration" && conversationLength > 3) {
    return {
      escalation: "de_escalate",
      instruction: null,
    };
  }

  return { escalation: "maintain", instruction: null };
}
